import { useBusinessContext } from '@root/context/BusinessContext'
import { useQuery } from '@tanstack/react-query'
import { fetchHeaders } from '../headers'
import { toHttpError } from '../root'

const HEADER_EXISTENCE_PARAMS = { page_size: 1 }

// Header list answers 400 when the business has no HeaderModel rows.
const NO_HEADERS_STATUS = 400

const isNoHeadersResponse = (error: unknown) => {
  try {
    return toHttpError(error).status === NO_HEADERS_STATUS
  } catch {
    return false
  }
}

/**
 * Whether this business has at least one account header.
 * Undefined while the check is still in flight.
 */
export const useBusinessHasHeaders = (): boolean | undefined => {
  const { slug } = useBusinessContext()

  const { data, isSuccess, isError } = useQuery({
    queryKey: [slug, 'headers', HEADER_EXISTENCE_PARAMS],
    queryFn: async () => {
      try {
        return await fetchHeaders({ slug }, HEADER_EXISTENCE_PARAMS)
      } catch (error) {
        if (isNoHeadersResponse(error)) {
          return { count: 0, next: null, prev: null, results: [] }
        }
        throw error
      }
    },
    enabled: Boolean(slug),
    staleTime: 60 * 1000
  })

  if (isSuccess) return (data?.count ?? 0) > 0
  if (isError) return false
  return undefined
}
